/**
 * Decision-first PDP buy box + offer-repeat + recommended products handlers.
 * - Purchase options: Subscribe & Save (default) vs one-time
 * - Cold primary CTA: product (+ selling_plan) + membership via MonthleesMembershipBundle
 * - Guest / member / pending: form submit (cart drawer) with selling_plan when selected
 * - Recommended cards: cold = add with membership; member/pending = product-only add
 * - Membership removal: cart/change → reprice → full reload
 */
(function () {
  'use strict';

  function moneyFormat() {
    var cfg = window.MonthleesMemberPricingConfig || {};
    return cfg.moneyFormat || '${{amount}}';
  }

  function formatMoney(cents) {
    var bundle = window.MonthleesMembershipBundle;
    if (bundle && typeof bundle.formatMoney === 'function') {
      return bundle.formatMoney(cents, moneyFormat());
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function membershipConfigFromRoot(root) {
    var globalCfg = window.MonthleesMemberPricingConfig || {};
    return {
      membershipProductId: globalCfg.membershipProductId || null,
      membershipProductIds: globalCfg.membershipProductIds || [],
      membershipMonthlyVariantId:
        root.getAttribute('data-membership-variant-id') ||
        globalCfg.attachVariantId ||
        globalCfg.membershipMonthlyVariantId ||
        null,
      membershipMonthlyPlanId:
        root.getAttribute('data-membership-plan-id') ||
        globalCfg.attachPlanId ||
        globalCfg.membershipMonthlyPlanId ||
        null,
      membershipAnnualVariantId: globalCfg.membershipAnnualVariantId || null,
      membershipAnnualPlanId: globalCfg.membershipAnnualPlanId || null,
      moneyFormat: moneyFormat(),
      cartUrl: '/cart',
      designMode: !!(window.Shopify && window.Shopify.designMode),
    };
  }

  function getVariantId(root, button) {
    if (button) {
      var fromBtn = button.getAttribute('data-variant-id');
      if (fromBtn) return fromBtn;
      var card = button.closest('[data-monthlees-rec-card]');
      if (card) {
        var cardVid = card.getAttribute('data-variant-id');
        if (cardVid) return cardVid;
      }
    }
    var select = root.querySelector('select[name="id"]');
    if (select) return select.value;
    var hidden = root.querySelector('input[name="id"]');
    if (hidden) return hidden.value;
    return root.getAttribute('data-variant-id');
  }

  function getQuantity(root, button) {
    if (button && button.closest('[data-monthlees-rec-card]')) return 1;
    var input = root.querySelector('[data-monthlees-pdp-quantity]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function getPurchaseOptions(root) {
    return root.querySelector('[data-monthlees-purchase-options]');
  }

  function isSubscriptionSelected(root) {
    var opts = getPurchaseOptions(root);
    if (!opts) return false;
    var checked = opts.querySelector('input[data-purchase-option-value]:checked');
    if (checked) return checked.value === 'subscribe';
    return opts.getAttribute('data-default-option') !== 'once';
  }

  function getSelectedSellingPlanId(root) {
    if (!isSubscriptionSelected(root)) return null;
    var opts = getPurchaseOptions(root);
    if (!opts) return null;
    return opts.getAttribute('data-selling-plan-id') || null;
  }

  function getSelectedProductCents(root) {
    var opts = getPurchaseOptions(root);
    var memberOt = parseInt(root.getAttribute('data-member-cents') || '0', 10) || 0;
    if (!opts) return memberOt;
    var sub = isSubscriptionSelected(root);
    var subCents = parseInt(opts.getAttribute('data-member-sub-cents') || '0', 10) || 0;
    var otCents = parseInt(opts.getAttribute('data-member-ot-cents') || '0', 10) || memberOt;
    return sub ? subCents || otCents : otCents;
  }

  function parseCentsOrNull(raw) {
    if (raw === '' || raw == null) return null;
    var n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }

  function setError(root, message, button) {
    var scope = (button && button.closest('[data-monthlees-rec-card]')) || root;
    var el = scope.querySelector('[data-monthlees-form-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function syncSellingPlanInput(root) {
    var opts = getPurchaseOptions(root);
    if (!opts) return;
    var planInput = opts.querySelector('[data-monthlees-selling-plan]');
    if (!planInput) return;
    var planId = opts.getAttribute('data-selling-plan-id') || '';
    var subscribe = isSubscriptionSelected(root);

    opts.querySelectorAll('[data-purchase-option]').forEach(function (label) {
      label.classList.toggle(
        'is-selected',
        label.getAttribute('data-purchase-option') === (subscribe ? 'subscribe' : 'once')
      );
    });

    if (subscribe && planId) {
      planInput.disabled = false;
      planInput.value = planId;
      planInput.removeAttribute('disabled');
    } else {
      planInput.value = '';
      planInput.disabled = true;
    }

    var ladder = root.querySelector('[data-monthlees-price-ladder]') || document.querySelector('[data-monthlees-price-ladder]');
    if (ladder) {
      ladder.setAttribute('data-purchase-subscription', subscribe ? 'true' : 'false');
    }

    document.dispatchEvent(
      new CustomEvent('monthlees:purchase-option-change', {
        detail: { subscription: subscribe, sellingPlanId: subscribe ? planId : null, root: root },
      })
    );
  }

  // The price ladder's "Save $X" pill and struck guest price (snippets/monthlees-price-ladder-decision).
  // Saving = the guest one-time price minus the member price of the selected option (subscribe or
  // one-time), the same data the buy box already carries; times the quantity. None → both hidden.
  function syncSavingsLine(root) {
    var ladder = root.querySelector('[data-monthlees-price-ladder]') || document.querySelector('[data-monthlees-price-ladder]');
    if (!ladder) return;
    var pill = ladder.querySelector('[data-monthlees-save-pill]');
    var guestLine = ladder.querySelector('[data-monthlees-guest-line]');
    var opts = getPurchaseOptions(root);
    var unitMember;
    var guestOt;
    if (opts) {
      var sub = isSubscriptionSelected(root);
      var memberOt = parseInt(opts.getAttribute('data-member-ot-cents') || '0', 10) || 0;
      var memberSub = parseInt(opts.getAttribute('data-member-sub-cents') || '0', 10) || 0;
      unitMember = sub ? memberSub || memberOt : memberOt;
      guestOt = parseInt(opts.getAttribute('data-guest-ot-cents') || '0', 10) || 0;
    } else {
      unitMember = parseInt(ladder.getAttribute('data-member-price-cents') || '0', 10) || 0;
      guestOt = parseInt(ladder.getAttribute('data-guest-cents') || '0', 10) || 0;
    }
    var unitSaving = unitMember > 0 && guestOt > unitMember ? guestOt - unitMember : 0;
    var qty = getQuantity(root);
    if (pill) {
      if (unitSaving > 0) {
        pill.textContent = 'Save ' + formatMoney(unitSaving * qty) + (qty > 1 ? ' on ' + qty : '');
        pill.hidden = false;
      } else {
        pill.textContent = '';
        pill.hidden = true;
      }
    }
    if (guestLine) guestLine.hidden = !(unitSaving > 0);

    // The cold-state member-math callout says "You’re saving $X on this order." in its trial and
    // multiple states (snippets/monthlees-member-math); keep that figure equal to the pill's.
    var mm = root.querySelector('[data-monthlees-member-math]');
    var mmState = mm && mm.getAttribute('data-mm-state');
    if (mm && unitSaving > 0 && (mmState === 'trial' || mmState === 'multiple')) {
      var head = mm.querySelector('.monthlees-member-math__headline');
      if (head) head.textContent = 'You’re saving ' + formatMoney(unitSaving * qty) + ' on this order.';
    }
  }

  function syncPurchaseOptionPrices(root) {
    var opts = getPurchaseOptions(root);
    if (!opts) return;

    var subscribe = isSubscriptionSelected(root);
    var memberOt = parseInt(opts.getAttribute('data-member-ot-cents') || '0', 10) || 0;
    var memberSub = parseInt(opts.getAttribute('data-member-sub-cents') || '0', 10) || 0;
    var guestOt = parseInt(opts.getAttribute('data-guest-ot-cents') || '0', 10) || 0;
    var productCents = subscribe ? memberSub : memberOt;
    var saving = guestOt > productCents ? guestOt - productCents : 0;
    var savingPct = guestOt > 0 && saving > 0 ? Math.round((saving * 100) / guestOt) : 0;

    var hero = root.querySelector('[data-monthlees-member-price]');
    if (hero && productCents > 0) hero.textContent = formatMoney(productCents);

    syncSavingsLine(root);

    var state = root.getAttribute('data-pdp-buy-state');
    var orderTotal = root.querySelector('[data-monthlees-order-total]');
    var membershipCents = parseInt(root.getAttribute('data-membership-cents') || '0', 10) || 0;
    var shippingCents = null;
    if (orderTotal) {
      membershipCents = parseInt(orderTotal.getAttribute('data-membership-cents') || '0', 10) || 0;
      shippingCents = parseCentsOrNull(orderTotal.getAttribute('data-shipping-cents'));
      var payToday = productCents + membershipCents;
      if (shippingCents != null) payToday += shippingCents;
      orderTotal.setAttribute('data-product-cents', String(productCents));
      orderTotal.setAttribute('data-pay-today-cents', String(payToday));
      var payEl = orderTotal.querySelector('[data-monthlees-pay-today]');
      if (payEl) payEl.textContent = formatMoney(payToday);
    }

    // The CTAs are synced whether or not an order-total block renders. The main
    // product.monthlees template has no order_total block, so gating these on it left
    // the join button quoting the subscribe price after "One-time purchase" was chosen.
    if (state === 'cold') {
      var joinBtn = root.querySelector('[data-monthlees-add-with-membership]');
      // A sold-out button renders disabled with the sold-out label; leave it alone.
      // data-cta-shipping-cents is the purchase form's own shipping setting, which the
      // Liquid label used; without it, fall back to the order-total block's.
      if (joinBtn && !joinBtn.disabled) {
        var ctaShipping = parseCentsOrNull(joinBtn.getAttribute('data-cta-shipping-cents'));
        if (ctaShipping == null) ctaShipping = shippingCents;
        var joinPay = productCents + membershipCents + (ctaShipping || 0);
        var span = joinBtn.querySelector('span');
        var label = 'Unlock member price · ' + formatMoney(joinPay) + ' today';
        if (span) span.textContent = label;
        else joinBtn.textContent = label;
      }
      if (orderTotal) {
        var guestBtn = root.querySelector('[data-monthlees-add-guest]');
        if (guestBtn) {
          var guestPay = guestOt;
          if (shippingCents != null) guestPay += shippingCents;
          guestBtn.textContent = 'Buy without membership · ' + formatMoney(guestPay) + ' today';
        }
      }
    }

    // Pending has no order-total block — keep ATC in sync with selected option.
    if (state === 'pending') {
      var pendingBtn = root.querySelector('[data-monthlees-add-product]');
      if (pendingBtn) {
        var pSpan = pendingBtn.querySelector('span');
        var pLabel = 'Add at member price · ' + formatMoney(productCents);
        if (pSpan) pSpan.textContent = pLabel;
        else pendingBtn.textContent = pLabel;
      }
    }

    syncMemberCtaLabel(root);
  }

  function handleAddWithMembership(root, button) {
    var bundle = window.MonthleesMembershipBundle;
    if (!bundle) {
      setError(root, 'Membership checkout is temporarily unavailable.', button);
      return;
    }

    setError(root, '', button);
    button.disabled = true;

    var config = membershipConfigFromRoot(root);
    var variantId = getVariantId(root, button);
    var qty = getQuantity(root, button);
    var sellingPlanId = getSelectedSellingPlanId(root);

    bundle
      .fetchCart()
      .then(function (cart) {
        config.skipMembershipAdd = bundle.membershipInCart(cart, config);
        var productContext = { variantId: variantId, quantity: qty };
        if (sellingPlanId) productContext.sellingPlanId = sellingPlanId;
        var built = bundle.buildCartItems(config, productContext, 'monthly');
        if (built.errors && built.errors.length) {
          throw new Error(built.errors[0]);
        }
        built.items.forEach(function (item) {
          var isMembership =
            String(item.id) === String(config.membershipMonthlyVariantId) ||
            String(item.id) === String(config.membershipAnnualVariantId);
          if (!isMembership) item.quantity = qty;
        });
        return bundle.addCartItemsSequential(built.items).then(function () {
          return bundle.openCartAfterAdd(config);
        });
      })
      .catch(function (err) {
        setError(root, (err && err.message) || 'Could not add to cart', button);
      })
      .finally(function () {
        button.disabled = false;
      });
  }

  function handleRecommendedAdd(root, button) {
    var variantId = getVariantId(root, button);
    if (!variantId) {
      setError(root, 'Could not add to cart', button);
      return;
    }

    setError(root, '', button);
    button.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not add to cart');
        return res.json();
      })
      .then(function () {
        var bundle = window.MonthleesMembershipBundle;
        var config = membershipConfigFromRoot(root);
        if (bundle && typeof bundle.openCartAfterAdd === 'function') {
          return bundle.openCartAfterAdd(config);
        }
        window.location.href = '/cart';
      })
      .catch(function (err) {
        setError(root, (err && err.message) || 'Could not add to cart', button);
      })
      .finally(function () {
        button.disabled = false;
      });
  }

  function syncMemberCtaLabel(root) {
    var state = root.getAttribute('data-pdp-buy-state');
    if (state !== 'member') return;
    var btn = root.querySelector('[data-monthlees-add-product]');
    if (!btn) return;
    var memberCents = getSelectedProductCents(root);
    var qty = getQuantity(root);
    var total = memberCents * qty;
    var span = btn.querySelector('span');
    var label = 'Add to cart · ' + formatMoney(total);
    if (span) span.textContent = label;
    else btn.textContent = label;

    var subtotal = root.querySelector('[data-monthlees-qty-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(total);
  }

  function removeMembershipAndReload(lineKey) {
    var bundle = window.MonthleesMembershipBundle;
    var globalCfg = window.MonthleesMemberPricingConfig || {};
    if (!bundle || !lineKey) {
      window.location.reload();
      return;
    }

    var config = {
      membershipProductId: globalCfg.membershipProductId || null,
      membershipProductIds: globalCfg.membershipProductIds || [],
    };

    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function () {
        return bundle.repriceAfterMembershipRemoved(config);
      })
      .then(function () {
        window.location.reload();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function bindRoot(root) {
    if (!root || root.getAttribute('data-monthlees-buybox-bound') === 'true') return;
    root.setAttribute('data-monthlees-buybox-bound', 'true');

    root.addEventListener('click', function (evt) {
      var joinBtn = evt.target.closest('[data-monthlees-add-with-membership]');
      if (joinBtn && root.contains(joinBtn)) {
        evt.preventDefault();
        handleAddWithMembership(root, joinBtn);
        return;
      }

      var recBtn = evt.target.closest('[data-monthlees-rec-add]');
      if (recBtn && root.contains(recBtn)) {
        evt.preventDefault();
        handleRecommendedAdd(root, recBtn);
        return;
      }

      var deltaBtn = evt.target.closest('[data-monthlees-qty-delta]');
      if (deltaBtn && root.contains(deltaBtn)) {
        evt.preventDefault();
        var input = root.querySelector('[data-monthlees-pdp-quantity]');
        if (!input) return;
        var delta = parseInt(deltaBtn.getAttribute('data-monthlees-qty-delta'), 10) || 0;
        var next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
        input.value = String(next);
        syncMemberCtaLabel(root);
        syncSavingsLine(root);
      }
    });

    root.addEventListener('change', function (evt) {
      var target = evt.target;
      if (!target || !root.contains(target)) return;
      if (target.getAttribute('data-purchase-option-value')) {
        syncSellingPlanInput(root);
        syncPurchaseOptionPrices(root);
        return;
      }
      if (target.hasAttribute('data-monthlees-pdp-quantity')) {
        syncMemberCtaLabel(root);
        syncSavingsLine(root);
      }
    });

    root.addEventListener('input', function (evt) {
      if (evt.target && evt.target.hasAttribute && evt.target.hasAttribute('data-monthlees-pdp-quantity')) syncSavingsLine(root);
    });

    syncSellingPlanInput(root);
    syncPurchaseOptionPrices(root);
    syncSavingsLine(root);
    syncMemberCtaLabel(root);
  }

  function bindRemovalButtons(scope) {
    (scope || document).querySelectorAll('[data-monthlees-remove-membership]').forEach(function (btn) {
      if (btn.getAttribute('data-bound') === 'true') return;
      btn.setAttribute('data-bound', 'true');
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        var key =
          btn.getAttribute('data-line-key') ||
          (btn.closest('[data-membership-line-key]') &&
            btn.closest('[data-membership-line-key]').getAttribute('data-membership-line-key'));
        removeMembershipAndReload(key);
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-monthlees-decision-buybox]').forEach(bindRoot);
    bindRemovalButtons(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  window.MonthleesPdpBuybox = {
    removeMembershipAndReload: removeMembershipAndReload,
    init: init,
  };
})();
